import { type Static, Type } from "@sinclair/typebox"
import {
  AuthoredCostInputsSchema,
  CardStatsSchema,
  TextOverridesSchema,
} from "./cards"
import { CardColorSchema } from "./colors"
import { IdSchema, NonEmptyStringSchema } from "./common"
import { CardArtworkSchema } from "./media"
import { SkillIdSchema, SkillModeSchema, SkillSelectionSchema } from "./skills"

export const HERO_LEVEL_UP_COSTS = { 2: 3, 3: 6 } as const

export const HeroLevelDeltaSchema = Type.Object({
  attackDelta: Type.Optional(Type.Integer()),
  healthDelta: Type.Optional(Type.Integer()),
  addedSkillIds: Type.Optional(Type.Array(SkillIdSchema)),
  upgradedSkills: Type.Optional(
    Type.Array(
      Type.Object({
        skillId: SkillIdSchema,
        fromMode: Type.Optional(SkillModeSchema),
        toMode: Type.Optional(SkillModeSchema),
        fromPayloadLevel: Type.Optional(
          Type.Integer({
            minimum: 1,
          }),
        ),
        toPayloadLevel: Type.Optional(
          Type.Integer({
            minimum: 1,
          }),
        ),
      }),
    ),
  ),
  textChangeSummary: Type.Optional(NonEmptyStringSchema),
})

export type HeroLevelDelta = Static<typeof HeroLevelDeltaSchema>

export const HeroCardShapeSchema = Type.Object({
  stats: CardStatsSchema,
  selectedSkills: Type.Array(SkillSelectionSchema, {
    maxItems: 3,
  }),
  artwork: Type.Optional(CardArtworkSchema),
  authoredCostInputs: AuthoredCostInputsSchema,
  derivedCost: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
  textOverrides: Type.Optional(TextOverridesSchema),
})

export type HeroCardShape = Static<typeof HeroCardShapeSchema>

export const HeroLevelDefinitionSchema = Type.Object({
  level: Type.Union([Type.Literal(1), Type.Literal(2), Type.Literal(3)]),
  displayName: Type.Optional(NonEmptyStringSchema),
  targetCost: Type.Integer({
    minimum: 0,
  }),
  card: HeroCardShapeSchema,
  deltaFromPrevious: Type.Optional(HeroLevelDeltaSchema),
})

export type HeroLevelDefinition = Static<typeof HeroLevelDefinitionSchema>

export const HeroDefinitionSchema = Type.Object({
  heroId: IdSchema,
  slug: NonEmptyStringSchema,
  name: NonEmptyStringSchema,
  primaryColor: CardColorSchema,
  secondaryColor: Type.Optional(CardColorSchema),
  portraitRef: Type.Optional(NonEmptyStringSchema),
  tags: Type.Optional(Type.Array(NonEmptyStringSchema)),
  flavorText: Type.Optional(NonEmptyStringSchema),
  progression: Type.Tuple([
    HeroLevelDefinitionSchema,
    HeroLevelDefinitionSchema,
    HeroLevelDefinitionSchema,
  ]),
  authoredByPlayerId: Type.Optional(IdSchema),
})

export type HeroDefinition = Static<typeof HeroDefinitionSchema>
